import type {
  CrmAccountStatus,
  CrmActivityType,
  CrmMeetingKind,
  CrmMeetingStatus,
  CrmOpportunityStage,
  CrmPartner,
  CrmPriority,
  CrmRevenueKind,
  CrmRevenueStatus,
  CrmSector,
  CrmSource,
  CrmTaskStatus,
  CrmTaskType,
} from "@shared/types";
import { toDate } from "./format";

type BadgeVariant = "neutral" | "primary" | "success" | "warning" | "danger" | "live";

/** Ordre du pipeline, de gauche à droite dans le kanban. */
export const CRM_STATUS_ORDER: CrmAccountStatus[] = [
  "prospect",
  "en_cours",
  "active",
  "en_service",
  "suspendu",
  "perdu",
];

export const CRM_STATUS_LABELS: Record<CrmAccountStatus, string> = {
  prospect: "Prospect",
  en_cours: "En cours",
  active: "Activé",
  en_service: "En service",
  suspendu: "Suspendu",
  perdu: "Perdu",
};

/** Variante de Badge par statut. */
export const CRM_STATUS_VARIANT: Record<CrmAccountStatus, BadgeVariant> = {
  prospect: "neutral",
  en_cours: "warning",
  active: "primary",
  en_service: "success",
  suspendu: "warning",
  perdu: "danger",
};

export const CRM_SECTOR_LABELS: Record<CrmSector, string> = {
  restaurant: "Restaurant",
  garage: "Garage",
  velo: "Atelier vélo",
  salon: "Salon / Bien-être",
  consulting: "Consultation / Agence B2B",
  custom: "Autre",
};

export const CRM_SOURCE_LABELS: Record<CrmSource, string> = {
  referral: "Recommandation",
  outbound: "Prospection",
  inbound: "Entrant",
  partenaire: "Partenaire",
  evenement: "Événement",
  autre: "Autre",
};

export const CRM_PARTNER_LABELS: Record<CrmPartner, string> = {
  umbeli: "Umbeli",
  maple_coast: "Maple & Coast",
  partage: "Partagé",
};

export const CRM_TASK_TYPE_LABELS: Record<CrmTaskType, string> = {
  appel: "Appel",
  email: "Courriel",
  relance: "Relance",
  reunion: "Réunion",
  demo: "Démo",
  admin: "Administratif",
  autre: "Autre",
};

export const CRM_TASK_STATUS_LABELS: Record<CrmTaskStatus, string> = {
  open: "À faire",
  done: "Terminé",
  cancelled: "Annulé",
};

export const CRM_PRIORITY_LABELS: Record<CrmPriority, string> = {
  low: "Basse",
  normal: "Normale",
  high: "Haute",
};

export const CRM_MEETING_KIND_LABELS: Record<CrmMeetingKind, string> = {
  visio: "Visioconférence",
  telephone: "Téléphone",
  sur_place: "Sur place",
  autre: "Autre",
};

export const CRM_MEETING_STATUS_LABELS: Record<CrmMeetingStatus, string> = {
  planifie: "Planifié",
  tenu: "Tenu",
  annule: "Annulé",
  no_show: "Absence",
};

export const CRM_STAGE_LABELS: Record<CrmOpportunityStage, string> = {
  qualification: "Qualification",
  proposition: "Proposition",
  negociation: "Négociation",
  gagnee: "Gagnée",
  perdue: "Perdue",
};

export const CRM_REVENUE_KIND_LABELS: Record<CrmRevenueKind, string> = {
  abonnement: "Abonnement",
  installation: "Installation",
  usage: "Usage",
  ponctuel: "Ponctuel",
  ajustement: "Ajustement",
};

export const CRM_REVENUE_STATUS_LABELS: Record<CrmRevenueStatus, string> = {
  prevu: "Prévu",
  facture: "Facturé",
  encaisse: "Encaissé",
  annule: "Annulé",
};

export const CRM_ACTIVITY_LABELS: Record<CrmActivityType, string> = {
  note: "Note",
  status_change: "Changement de statut",
  field_change: "Modification",
  task: "Suivi",
  meeting: "Rendez-vous",
  opportunity: "Opportunité",
  revenue: "Revenu",
  tenant_link: "Liaison au tenant",
  system: "Système",
  appel: "Appel",
};

/** Formatage monétaire fr-CA. 123456 -> "1 234,56 $". */
export function formatCents(cents: number, currency = "CAD"): string {
  try {
    return new Intl.NumberFormat("fr-CA", { style: "currency", currency }).format(cents / 100);
  } catch {
    // Intl lève sur un code hors ISO 4217 : une ligne héritée d'un import ne doit
    // pas faire écran blanc, elle s'affiche avec son code de devise brut.
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/** Saisie utilisateur -> cents. Accepte "1 234,56", "1234.56", "1,234.56". null si vide/invalide. */
export function parseAmountToCents(input: string): number | null {
  // \s couvre les espaces fines et insécables produites par le formatage fr-CA.
  const cleaned = input.replace(/(CAD|USD)/gi, "").replace(/[\s$]/g, "");
  if (!cleaned) return null;

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let normalized: string;
  if (lastComma >= 0 && lastDot >= 0) {
    // Les deux séparateurs présents : le dernier rencontré porte les décimales.
    const decimal = lastComma > lastDot ? "," : ".";
    const thousands = decimal === "," ? "." : ",";
    normalized = cleaned.split(thousands).join("").replace(decimal, ".");
  } else if (lastComma >= 0) {
    // "1,23" est un montant fr-CA, "1,234" un séparateur de milliers anglais.
    const decimals = cleaned.length - lastComma - 1;
    normalized =
      decimals > 0 && decimals <= 2 ? cleaned.replace(",", ".") : cleaned.split(",").join("");
  } else {
    normalized = cleaned;
  }

  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  const magnitude = Math.round(Math.abs(value) * 100);
  return value < 0 ? -magnitude : magnitude;
}

/** cents -> valeur d'input texte éditable ("1234.56"), "" si 0 et allowEmpty. */
export function centsToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || cents === 0) return "";
  return (cents / 100).toFixed(2);
}

/** true si la date est passée. Utilise toDate() de lib/format. */
export function isOverdue(due: string | number | null | undefined): boolean {
  const d = toDate(due);
  return d !== null && d.getTime() < Date.now();
}

/** "dans 3 jours" / "il y a 2 jours" / "aujourd'hui" — Intl.RelativeTimeFormat fr-CA. */
export function formatRelativeDay(value: string | number | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  // Écart en jours calendaires : « demain 8 h » ne doit pas s'afficher « dans 0 jour ».
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(d) - startOfDay(new Date())) / 86_400_000);
  return new Intl.RelativeTimeFormat("fr-CA", { numeric: "auto" }).format(days, "day");
}

/** Valeur pour <input type="datetime-local"> à partir d'un timestamp API. */
export function toDateTimeLocalValue(value: string | number | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Inverse : valeur d'input datetime-local -> ISO à envoyer à l'API (null si vide). */
export function dateTimeLocalToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
